import type { AxiosResponse } from 'axios';
import { useMutation, useQueryClient } from 'react-query';

import { TAG_API_ROUTES } from '@/api-routes';
import { axiosInstance } from '@/axios';
import { QUERY_KEY } from '@/components/UserTags/common';
import { useInvalidateNeedlbox } from '@/hooks/query/useFetchNeedlbox';

export const useMutateTags = () => {
  const queryClient = useQueryClient();
  const invalidateNeedlbox = useInvalidateNeedlbox();

  return useMutation(
    ({ tag }: MutationType) => {
      return axiosInstance.post<void>(TAG_API_ROUTES.index(), {
        tag,
      });
    },
    {
      onMutate: async ({ tag }) => {
        const previousUserTagsData = queryClient.getQueryData<DataType>([
          QUERY_KEY,
          'user-tags',
        ]);

        if (!previousUserTagsData) {
          return;
        }

        queryClient.setQueryData<DataType>([QUERY_KEY, 'user-tags'], {
          ...previousUserTagsData,
          data: {
            ...previousUserTagsData.data,
            results: [
              ...new Set([...previousUserTagsData.data.results, tag]),
            ].sort((a, b) => a.localeCompare(b)),
          },
        });

        return { previousUserTagsData };
      },
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries([QUERY_KEY, 'user-tags']),
          queryClient.invalidateQueries([QUERY_KEY, 'filters']),
          invalidateNeedlbox(),
        ]);
      },
      onError: (
        _,
        __,
        context?: {
          previousUserTagsData?: DataType;
        }
      ) => {
        if (!context) {
          return;
        }

        queryClient.setQueryData(
          [QUERY_KEY, 'user-tags'],
          context.previousUserTagsData
        );
      },
    }
  );
};

type MutationType = {
  tag: string;
};

type ResponseType = {
  results: string[];
};
export type DataType = AxiosResponse<ResponseType>;
